import { BLOCK, AWAIT, LOCAL_ACTIONS, CLIENT_EVENTS } from "./agentContract";
import userDetail from "Stores/userDetail";
import deepResearchData from "Stores/deepResearchData";
import onboardingCopilotStore from "Stores/onboardingCopilotStore";
import { sortFindings } from "./findingsUtils";
import { connectStatus } from "./onboardingServices";
import { fetchConnectedEmails, fetchLinkedInAccounts } from "Utils/commonAPI";
import { MXP_EVENT } from "Static/MixPanelEvents";
import buildOnboardingScenario, { emitSellingQuestion, signupDomain } from "./onboardingScenario";

/**
 * Existing users get triage, not setup (spec: "existing user = it already
 * knows my account"). A one-time account digest: the top account-level
 * findings ranked by the tone ladder, each with its action, then section
 * starters. Branches: missing seller profile, dormant account, nothing
 * actionable (skip the digest rather than invent findings).
 *
 * Scope note: this ranks account-level signals already in the stores. Running
 * every per-section findings rule (inbox/tasks/reports/...) needs a per-section
 * data-gathering layer that does not exist yet; that aggregation is a
 * follow-up. The tone ladder and render path here are the shipped ones.
 */

const firstName = () => userDetail?.userInfo?.firstName || "there";
const track = (event, props = {}) => {
  try {
    if (window && window.mixpanel) window.mixpanel.track(event, props);
  } catch (e) {
    /* best-effort */
  }
};

const DAY = 24 * 60 * 60 * 1000;

// "I looked at your account" has to be backed by what it saw. Builds the
// "{n} contacts, {m} sequences, inbox connected" phrase from whatever counts
// the stores already hold; empty when nothing is known.
const accountFacts = ({ contactCount, seqCount, emailConnected }) => {
  const bits = [];
  if (contactCount) bits.push(`${contactCount.toLocaleString()} contact${contactCount === 1 ? "" : "s"}`);
  if (seqCount) bits.push(`${seqCount.toLocaleString()} sequence${seqCount === 1 ? "" : "s"}`);
  if (emailConnected) bits.push("inbox connected");
  return bits.join(", ");
};

const lastActivityTs = () => {
  const ff = userDetail?.userFeatureFlag || {};
  return ff.lastActivityTs || ff.lastActiveTs || ff.lastLoginTs || 0;
};

const buildAccountFindings = ({ hasProfile, emailConnected, seqCount }) => {
  const nav = (path, label) => [{ label, run: { navigate: path } }];
  const out = [];
  if (!emailConnected) {
    out.push({
      tone: "bad",
      key: "no_inbox",
      label: "No email inbox is connected",
      text: "Your sequences can't send until one is. Connecting takes under a minute.",
      actions: nav("/settings/account", "Connect inbox")
    });
  }
  if (!seqCount) {
    out.push({
      tone: "warn",
      key: "no_sequence",
      label: "No sequences yet",
      text: "Your contacts are waiting on a first campaign. I can build one from a saved list.",
      actions: nav("/sequences/all", "Create a sequence")
    });
  }
  return out;
};

export const emitDigest = async api => {
  const turn = api.beginTurn();
  // Load the account signals the digest reasons over before reading them, so
  // it does not disagree with the onboarding flow about a connected inbox.
  await Promise.all([
    deepResearchData.fetchSellerProfiles().catch(() => {}),
    Promise.resolve(fetchConnectedEmails && fetchConnectedEmails()).catch(() => {}),
    Promise.resolve(fetchLinkedInAccounts && fetchLinkedInAccounts()).catch(() => {})
  ]);
  const hasProfile = (deepResearchData.sellerProfiles || []).length > 0;
  const status = connectStatus();
  const ff = userDetail?.userFeatureFlag || {};
  const seqCount = ff.noOfSequencesUsed || 0;
  const contactCount = ff.noOfContactsUsed || ff.noOfContactsAddedInTeam || 0;
  const facts = accountFacts({ contactCount, seqCount, emailConnected: status.email });
  const domain = signupDomain();
  const ts = lastActivityTs();
  const dormant = ts && Date.now() - ts > 30 * DAY;

  // Dormant branch: a restart offer instead of the digest.
  if (dormant) {
    const month = new Date(ts).toLocaleString(undefined, { month: "long" });
    track(MXP_EVENT.DIGEST_SHOWN, { branch: "dormant" });
    await turn.text(
      `Welcome back, ${firstName()}. Your last campaign ran in ${month}. Want me to find fresh contacts for your ICP and restart?`
    );
    turn.addBlock(BLOCK.CTA_CHIPS, {
      chips: [
        { actionId: LOCAL_ACTIONS.NAVIGATE, path: "/list-builder", label: "Find fresh contacts", style: "primary" },
        { actionId: "ob_dismiss", label: "Not now" }
      ]
    });
    turn.end(AWAIT.CTA);
    onboardingCopilotStore.complete();
    return;
  }

  // Contacts but nothing done with them yet: one question, three one-click
  // answers (each sent to the live copilot as the request), not a findings list.
  if (contactCount > 0) {
    track(MXP_EVENT.DIGEST_SHOWN, { branch: "next_action" });
    const n = contactCount.toLocaleString();
    turn.addBlock(BLOCK.CTA_CHIPS, {
      title: `Hi ${firstName()}. You have ${n} contacts. What would you like to do with them?`,
      layout: "options",
      chips: [
        {
          actionId: LOCAL_ACTIONS.ASK,
          icon: "send",
          label: "Create a campaign for them",
          description: "I draft the emails from your seller profile. Nothing sends until you say so.",
          meta: "Free",
          style: "primary",
          prompt: "Create an email campaign for the contacts in my latest list."
        },
        {
          actionId: LOCAL_ACTIONS.ASK,
          icon: "mail",
          label: "Find emails",
          description: "Verified work emails, so your campaign can reach them.",
          meta: "1 credit each",
          prompt: "Find emails for the contacts in my latest list."
        },
        {
          actionId: LOCAL_ACTIONS.ASK,
          icon: "call",
          label: "Find phone numbers",
          description: "Direct dials and mobiles for cold calling.",
          meta: "10 credits each",
          prompt: "Find phone numbers for the contacts in my latest list so I can call them."
        }
      ]
    });
    turn.end(AWAIT.CTA);
    onboardingCopilotStore.complete();
    return;
  }

  const findings = sortFindings(buildAccountFindings({ hasProfile, emailConnected: status.email, seqCount })).slice(0, 3);

  // Nothing actionable: a short greeting, never an invented finding.
  if (!findings.length && hasProfile) {
    await turn.text(
      `Hi ${firstName()}. I looked at your account${facts ? `: ${facts}` : ""}. Everything looks in good shape. Ask me anything about it and I'll dig in.`
    );
    turn.end(AWAIT.USER_INPUT);
    onboardingCopilotStore.complete();
    return;
  }

  // Missing profile, nothing else wrong: say what was seen, what is missing,
  // and why the one button follows. The profile is built in-chat (below).
  if (!findings.length && !hasProfile) {
    track(MXP_EVENT.DIGEST_SHOWN, { branch: "missing_profile" });
    await turn.text(
      `Hi ${firstName()}. I looked at your account${facts ? `: ${facts}` : ""}. What's missing is a seller profile, so I can't personalise research or messaging for you yet. Give me your website and I'll build it in about a minute.`
    );
    turn.addBlock(BLOCK.CTA_CHIPS, {
      chips: [
        { actionId: "ob_build_icp", label: domain ? `Build my profile from ${domain}` : "Build my profile", style: "primary" },
        { actionId: "ob_dismiss", label: "Not now" }
      ]
    });
    turn.end(AWAIT.CTA);
    onboardingCopilotStore.complete();
    return;
  }

  track(MXP_EVENT.DIGEST_SHOWN, { branch: "digest", count: findings.length });
  await turn.text(
    `Hi ${firstName()}. I looked at your account${facts ? `: ${facts}` : ""}. Here's where I'd start:`
  );
  const bullets = findings.map(f => `- **${f.label}.** ${f.text}`).join("\n");
  if (bullets) await turn.text(bullets);

  const chips = findings
    .map(f => f.actions && f.actions[0])
    .filter(a => a && a.run && a.run.navigate)
    .map(a => ({ actionId: LOCAL_ACTIONS.NAVIGATE, path: a.run.navigate, label: a.label }));

  // Missing-profile extra card alongside other findings. Handled in-chat, not
  // by navigating away: clicking it asks for the website here and builds the
  // profile in the conversation (see the digest scenario steps below).
  if (!hasProfile) {
    await turn.text(
      "Also: no seller profile yet, so research and messaging stay generic until there is one."
    );
    chips.push({
      actionId: "ob_build_icp",
      label: domain ? `Build my profile from ${domain}` : "Build my profile"
    });
  }
  if (chips.length) {
    chips[0].style = "primary";
    turn.addBlock(BLOCK.CTA_CHIPS, { chips });
  }
  turn.end(AWAIT.CTA);
  onboardingCopilotStore.complete();
};

// The digest is otherwise render-only, but "Build your ICP from your website"
// drops the user into the onboarding profile build in-chat (ask for the site,
// enrich, review, then continue) instead of navigating to the settings tab.
const buildDigestScenario = () => [
  {
    id: "digest_build_icp",
    expects: evt =>
      evt.type === CLIENT_EVENTS.CTA_CLICKED && evt.actionId === "ob_build_icp",
    run: async (api, evt, ctx) => {
      onboardingCopilotStore.begin("onboarding", userDetail?.userInfo?.memberId);
      return emitSellingQuestion(api, ctx);
    }
  },
  // Reuse the onboarding steps so website submit -> enrich -> profile review ->
  // ICP -> search -> ... all work from here.
  ...buildOnboardingScenario()
];

export default buildDigestScenario;
